import { useState, useEffect, useCallback, useRef } from 'react';

export interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down' | 'unknown';
  lastCheck: number;
  message?: string;
}

export interface SystemHealth {
  cpu: { percent: number; load: number; cores: number };
  memory: { percent: number; used: string; total: string };
  disk: { percent: number; used: string; total: string };
}

export interface HealthState {
  services: Record<string, ServiceHealth>;
  system: SystemHealth | null;
  lastUpdate: number;
}

interface HealthEvent {
  type: 'service_down' | 'service_recovered' | 'threshold_exceeded' | 'threshold_recovered';
  service?: string;
  metric?: string;
  value?: number;
  threshold?: number;
  message: string;
}

const LOG_SERVER = '/api/claude-logs';
const CHECK_INTERVAL = 5000; // 5 seconds

const THRESHOLDS = {
  memory: 80,
  cpu: 90,
  disk: 90
};

export const useHealthMonitor = (onHealthEvent?: (event: HealthEvent) => void) => {
  const [health, setHealth] = useState<HealthState>({
    services: {},
    system: null,
    lastUpdate: 0
  });

  const previousHealth = useRef<HealthState | null>(null);
  const cpuHighStart = useRef<number | null>(null);

  const checkServices = useCallback(async () => {
    const services: Record<string, ServiceHealth> = {};
    const now = Date.now();

    // Check log server
    try {
      const res = await fetch(`${LOG_SERVER}/health`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      services['log-server'] = {
        name: 'Log Server',
        status: data.status === 'ok' ? 'healthy' : 'degraded',
        lastCheck: now
      };
    } catch {
      services['log-server'] = { name: 'Log Server', status: 'down', lastCheck: now };
    }

    // Check orchestrator
    try {
      const res = await fetch(`${LOG_SERVER}/api/orchestrator/health`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      services['orchestrator'] = {
        name: 'Orchestrator',
        status: data.status === 'ok' ? 'healthy' : 'degraded',
        lastCheck: now
      };
    } catch {
      services['orchestrator'] = { name: 'Orchestrator', status: 'down', lastCheck: now };
    }

    // Check overseer
    try {
      const res = await fetch(`${LOG_SERVER}/api/overseer/status`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      services['overseer'] = {
        name: 'Overseer',
        status: data.running ? 'healthy' : 'down',
        lastCheck: now,
        message: data.running ? `PID: ${data.pid}` : 'Not running'
      };
    } catch {
      services['overseer'] = { name: 'Overseer', status: 'unknown', lastCheck: now };
    }

    return services;
  }, []);

  const checkSystemStats = useCallback(async (): Promise<SystemHealth | null> => {
    try {
      const res = await fetch(`${LOG_SERVER}/api/system-stats`, { signal: AbortSignal.timeout(3000) });
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  const detectChanges = useCallback((newHealth: HealthState) => {
    if (!previousHealth.current || !onHealthEvent) return;

    const prev = previousHealth.current;

    // Check service state changes
    for (const [id, service] of Object.entries(newHealth.services)) {
      const prevService = prev.services[id];
      if (prevService) {
        if (prevService.status !== 'down' && service.status === 'down') {
          onHealthEvent({
            type: 'service_down',
            service: service.name,
            message: `${service.name} is down`
          });
        } else if (prevService.status === 'down' && service.status === 'healthy') {
          onHealthEvent({
            type: 'service_recovered',
            service: service.name,
            message: `${service.name} recovered`
          });
        }
      }
    }

    // Check threshold changes
    if (newHealth.system && prev.system) {
      const { memory, cpu, disk } = newHealth.system;
      const prevMem = prev.system.memory.percent;
      const prevDisk = prev.system.disk.percent;

      // Memory threshold
      if (prevMem < THRESHOLDS.memory && memory.percent >= THRESHOLDS.memory) {
        onHealthEvent({
          type: 'threshold_exceeded',
          metric: 'memory',
          value: memory.percent,
          threshold: THRESHOLDS.memory,
          message: `Memory usage at ${memory.percent}%`
        });
      } else if (prevMem >= THRESHOLDS.memory && memory.percent < THRESHOLDS.memory) {
        onHealthEvent({
          type: 'threshold_recovered',
          metric: 'memory',
          value: memory.percent,
          threshold: THRESHOLDS.memory,
          message: `Memory usage normalized to ${memory.percent}%`
        });
      }

      // Disk threshold
      if (prevDisk < THRESHOLDS.disk && disk.percent >= THRESHOLDS.disk) {
        onHealthEvent({
          type: 'threshold_exceeded',
          metric: 'disk',
          value: disk.percent,
          threshold: THRESHOLDS.disk,
          message: `Disk usage at ${disk.percent}%`
        });
      }

      // CPU sustained high (60 seconds)
      if (cpu.percent >= THRESHOLDS.cpu) {
        if (!cpuHighStart.current) {
          cpuHighStart.current = Date.now();
        } else if (Date.now() - cpuHighStart.current >= 60000) {
          onHealthEvent({
            type: 'threshold_exceeded',
            metric: 'cpu',
            value: cpu.percent,
            threshold: THRESHOLDS.cpu,
            message: `CPU sustained above ${THRESHOLDS.cpu}% for 60+ seconds`
          });
          cpuHighStart.current = Date.now(); // Reset to avoid repeat alerts
        }
      } else {
        cpuHighStart.current = null;
      }
    }
  }, [onHealthEvent]);

  const refresh = useCallback(async () => {
    const [services, system] = await Promise.all([
      checkServices(),
      checkSystemStats()
    ]);

    const newHealth: HealthState = {
      services,
      system,
      lastUpdate: Date.now()
    };

    detectChanges(newHealth);
    previousHealth.current = newHealth;
    setHealth(newHealth);
  }, [checkServices, checkSystemStats, detectChanges]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [refresh]);

  return { health, refresh };
};

export default useHealthMonitor;
